import React from 'react';

import AnimatedContentCard from '../../components/Common/AnimatedContentCard.js';
import ContentCard from '../../components/Common/ContentCard';
import ContentfulImage from '../../components/Common/ContentfulImage';
import FloatingCallout from '../../components/Common/FloatingCallout';
import { ArrowLink } from '../../components/Common/Links';
import RichTextConverter from '../../components/Common/RichText/RichTextConverter';
import SmartLink from '../../components/Common/SmartLink';
import VisualBelowHeadline from '../../components/Common/VisualBelowHeadline.js';
import ProgramContentCard from '../../components/Generic/ProgramContentCard';
import CenteredTextOverlay from '../../components/PageSections/CenteredTextOverlay.js';
import HalfAndHalfContent from '../../components/PageSections/HalfAndHalfContent';
import HalfBGContentCard from '../../components/PageSections/HalfBGContentCard';
import PageSection from '../../components/PageSections/PageSection';
import { getThemeClass } from '../textTools.js';

export const ContentfulSectionContentSection = (section, i, styles, isCampaignLP) => {
  if (section.useFloatingLayout) {
    return <FloatingCallout key={i} section={section} />;
  }

  if (section.imageStyle === 'Half Content Section') {
    return <HalfAndHalfContent key={i} section={section} extended={!section.imageBackgroundGradient && isCampaignLP} />;
  }

  if (section.titleIsSectionHeader) {
    let location = section.headingLocation === 'Left' ? styles.headingLeft : '';
    return (
      <PageSection
        slim
        className={[section.backgroundColor === 'Grey' ? styles.grey : '', styles.sectionHeaderWrap].join(' ')}
        key={`title-${i}`}
        data-analytics-component={'section-header'}
        data-analytics-id={section.title}
      >
        <div className={[styles.sectionHeader, location].join(' ')}>
          <h2>{section.title}</h2>
        </div>
      </PageSection>
    );
  }

  let imageSide = section.imageLocation === true ? 'Left' : 'Right';
  if (section.imageLocation === undefined) {
    imageSide = i > 0 && i % 2 === 0 ? 'Left' : 'Right';
  }

  switch (section.imageStyle) {
    case 'Small Centered': {
      return <ProgramContentCard key={i} section={section} imageSide={imageSide} limitImageSize={true} />;
    }
    case 'Centered With Background': {
      return <HalfBGContentCard key={i} section={section} imageSide={imageSide} slideIn />;
    }
    case 'Scroll-Animated': {
      return <AnimatedContentCard key={i} section={section} />;
    }
    case 'Below Content with Bleed': {
      // TODO: move this into it's own component
      return (
        <PageSection
          key={i}
          slim
          wide
          fullBleed
          data-analytics-component={'content-card-with-bleed'}
          data-analytics-id={section.title}
        >
          <div className={styles.stacked}>
            <div className={styles.copy}>
              <h2>{section.title}</h2>
              <RichTextConverter copy={section.copy} />
            </div>
            {section.mainImage && (
              <>
                <ContentfulImage imageFragment={section.mainImage} alt={section.mainImage?.title} />
              </>
            )}
          </div>
        </PageSection>
      );
    }
    case 'Full Background': {
      // TODO: move this into it's own component
      return (
        <PageSection
          key={i}
          slim
          wide
          fullBleed
          className={[styles[section.backgroundColor], styles.fullBackground].join(' ')}
          data-analytics-component={'full-bg-content-card'}
          data-analytics-id={section.title}
        >
          {section.backgroundImage && (
            <div
              className={styles.backgroundImage}
              style={{
                '--backgroundImage': `url(${section.backgroundImage.file?.url})`,
              }}
            />
          )}
          <div className={styles.wrapper}>
            <div className={styles.copy}>
              {section.logo && <ContentfulImage lazy={true} imageFragment={section.logo} />}
              <h3>{section.title}</h3>
              {section.subtitle && <span className={styles.attribution}>{section.subtitle}</span>}
              {section.ctaUrl && <ArrowLink className={styles.cta} url={section.ctaUrl} label={section.ctaLabel} />}
            </div>
            {section.mainImage &&
              (section.ctaUrl ? (
                <SmartLink className={styles.image} to={section.ctaUrl}>
                  <ContentfulImage
                    lazy={true}
                    imageFragment={section.mainImage}
                    height={section.mainImage.height}
                    width={section.mainImage.width}
                  />
                </SmartLink>
              ) : (
                <div className={styles.image}>
                  <ContentfulImage
                    lazy={true}
                    imageFragment={section.mainImage}
                    height={section.mainImage.height}
                    width={section.mainImage.width}
                  />
                </div>
              ))}
          </div>
        </PageSection>
      );
    }
    case 'Centered Text Overlay': {
      return <CenteredTextOverlay section={section} key={i} />;
    }
    case 'Below Headline': {
      return <VisualBelowHeadline section={section} key={i} />;
    }
    default:
      break;
  }

  /*if (section.imageStyle === 'Half & Half') {
    return <HalfAndHalfContent section={section} imageSide={imageSide} />;
  }*/

  const themeClass = getThemeClass(section.colorTheme?.themeName);

  const backgroundColors = {
    White: styles.white,
    Grey: styles.grey,
    'Grey Gradient': styles.greyGradient,
    Blue: styles.blue,
    Purple: styles.purple,
    'Purple / Pink Gradient': styles.purplePinkGradient,
    Evergreen: styles.evergreen,
    Lilac: styles.lilac,
    'Lilac Deep': styles.lilacDeep,
  };

  return (
    <ContentCard
      key={i}
      slim
      backgroundImage={section.backgroundImage}
      anchorImage={section.imageStyle === 'Half & Half Image Anchored'}
      adjustForTallImage={section.adjustForTallImage}
      eyebrowText={section.subtitle}
      eyebrowColor={section.subtitleColor}
      heading={section.titleIsSectionHeader ? '' : section.title}
      image={section.mainImage}
      wistiaUrl={section.wistiaUrl}
      logo={section.logo}
      copy={section.copy}
      attachedContent={section.contentSection}
      ctaUrl={section.ctaUrl}
      ctaLabel={section.ctaLabel}
      newWindow={section.ctaOpensNewWindow}
      imageBleed={section.imageStyle === 'Bleed' || isCampaignLP}
      imageSide={imageSide}
      imageHeight={section.mainImage?.height}
      imageWidth={section.mainImage?.width}
      limitImageHeight
      className={[backgroundColors[section.backgroundColor] || ''].join(' ')}
      curve={section.hasCurve}
      animate={true}
      lazy={true}
      fullHeight={section.fullHeight}
      themeClass={themeClass}
    />
  );
};
